/**
 * MetaSetupWizard — Iter Feb 2026 R40.
 *
 * A guided, copy-paste-friendly wizard that walks the workspace admin
 * through registering Acquilix as a Meta App so Lead Ads can flow in.
 *
 * The backend endpoint `/workspaces/{ws}/perf/meta/setup-wizard` returns
 * the actual values (webhook URL, verify token, redirect URI, scopes,
 * curl test string). This page renders them alongside the App Review
 * checklist so users can go through Meta's approval flow in one screen.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Check, Copy, ExternalLink, Shield, Webhook, KeyRound, ClipboardList, AlertTriangle,
} from "lucide-react";

function CopyLine({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value || "");
      setCopied(true);
      toast.success(`Copied ${label}`);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Clipboard blocked"); }
  };
  return (
    <div className="border p-2 flex items-center gap-2" data-testid={testid}>
      <div className="min-w-32 text-[11px] uppercase tracking-widest text-muted-foreground shrink-0">{label}</div>
      <code className="text-xs font-mono flex-1 truncate select-all">{value || "—"}</code>
      <Button size="sm" variant="ghost" className="h-7 rounded-none" onClick={copy} disabled={!value}>
        {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
      </Button>
    </div>
  );
}

export default function MetaSetupWizard() {
  const { activeWorkspaceId } = useAuth();
  const [w, setW] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/perf/meta/setup-wizard`);
      setW(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load wizard")); }
    finally { setLoading(false); }
  }, [activeWorkspaceId]);
  useEffect(() => { load(); }, [load]);

  if (loading || !w) return <div className="p-6 text-sm text-muted-foreground">Loading wizard…</div>;

  return (
    <div className="flex flex-col h-full" data-testid="meta-setup-wizard-page">
      <PageHeader
        title="Meta App · Setup Wizard"
        description="Register Acquilix as an official Meta CRM app so Lead Ads flow in real-time. Copy each value into the Meta Developer console at the matching step."
        eyebrow="Performance · Meta"
      />

      <div className="px-4 sm:px-6 md:px-10 py-4 space-y-4 flex-1 overflow-auto max-w-5xl">

        <div className={`flex items-center gap-2 border p-3 ${w.meta_app_configured ? "border-emerald-400 bg-emerald-50 text-emerald-800" : "border-amber-400 bg-amber-50 text-amber-800"}`} data-testid="meta-wizard-status">
          {w.meta_app_configured
            ? <><Check className="w-4 h-4" /> Meta App configured — App ID {w.meta_app_id}. You can now run OAuth from Performance → Accounts.</>
            : <><AlertTriangle className="w-4 h-4" /> Meta App is NOT configured on the server yet. Set <code className="text-[11px]">META_APP_ID</code> / <code className="text-[11px]">META_APP_SECRET</code> / <code className="text-[11px]">META_REDIRECT_URI</code> as environment variables after finishing the steps below.</>}
        </div>

        {/* Step 1 — Create Meta App */}
        <StepCard step={1} icon={Shield} title="Create the Meta App" testid="meta-wizard-step-1">
          <ol className="list-decimal ml-5 text-sm space-y-1">
            <li>Go to <a href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">developers.facebook.com/apps <ExternalLink className="w-3 h-3" /></a> and click <b>Create App</b>.</li>
            <li>Choose <b>Business</b> as the use case.</li>
            <li>Name it <b>Acquilix</b> (or your agency name) and select your Business Manager.</li>
          </ol>
        </StepCard>

        {/* Step 2 — Basic settings */}
        <StepCard step={2} icon={ClipboardList} title="Fill Basic Settings" testid="meta-wizard-step-2">
          <p className="text-sm text-muted-foreground mb-2">Under <b>Settings → Basic</b>, set:</p>
          <div className="space-y-1">
            <CopyLine label="App Domains" value={new URL(w.webhook_callback_url).host} testid="meta-wizard-copy-domain" />
            <CopyLine label="Privacy Policy" value={`${window.location.origin}/legal/privacy`} testid="meta-wizard-copy-privacy" />
            <CopyLine label="Terms of Service" value={`${window.location.origin}/legal/terms`} testid="meta-wizard-copy-terms" />
            <CopyLine label="Data Deletion URL" value={w.data_deletion_url} testid="meta-wizard-copy-datadel" />
          </div>
        </StepCard>

        {/* Step 3 — Facebook Login for Business + Marketing API */}
        <StepCard step={3} icon={KeyRound} title="Add Products + OAuth Redirect" testid="meta-wizard-step-3">
          <p className="text-sm text-muted-foreground mb-2">Add these products from <b>App → Add Product</b>:</p>
          <ul className="list-disc ml-5 text-sm mb-3">
            <li>Facebook Login for Business</li>
            <li>Marketing API</li>
            <li>Webhooks</li>
          </ul>
          <p className="text-sm text-muted-foreground mb-2">Under <b>Facebook Login for Business → Settings</b>, paste this OAuth redirect URI:</p>
          <CopyLine label="OAuth Redirect URI" value={w.redirect_uri} testid="meta-wizard-copy-redirect" />
        </StepCard>

        {/* Step 4 — Webhooks */}
        <StepCard step={4} icon={Webhook} title="Configure Webhooks · leadgen" testid="meta-wizard-step-4">
          <p className="text-sm text-muted-foreground mb-2">
            Under <b>Webhooks → Page</b>, subscribe to the <code>leadgen</code> field. Paste:
          </p>
          <div className="space-y-1">
            <CopyLine label="Callback URL" value={w.webhook_callback_url} testid="meta-wizard-copy-cbk" />
            <CopyLine label="Verify Token" value={w.verify_token} testid="meta-wizard-copy-vt" />
          </div>
          <div className="mt-3 text-xs bg-neutral-50 border p-2">
            <div className="font-semibold mb-1">Test locally (from terminal):</div>
            <code className="block whitespace-pre-wrap break-all text-[10px]">{w.sample_curl_test}</code>
            <div className="text-muted-foreground mt-1">Should echo back the challenge string. If you get 403 → the token doesn&apos;t match; refresh this page.</div>
          </div>
        </StepCard>

        {/* Step 5 — Required scopes */}
        <StepCard step={5} icon={Shield} title="Request Advanced Access · 7 scopes" testid="meta-wizard-step-5">
          <p className="text-sm text-muted-foreground mb-2">
            Under <b>App Review → Permissions and Features</b>, request Advanced Access for each of these
            (Meta will ask for a screencast and business justification — the &ldquo;why&rdquo; column is your script):
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {w.required_scopes.map(s => (
              <div key={s.code} className="border p-2 text-xs" data-testid={`meta-wizard-scope-${s.code}`}>
                <code className="font-mono">{s.code}</code>
                <div className="text-muted-foreground mt-0.5">{s.why}</div>
              </div>
            ))}
          </div>
        </StepCard>

        {/* Step 6 — App Review Checklist */}
        <StepCard step={6} icon={ClipboardList} title="Business Verification + App Review" testid="meta-wizard-step-6">
          <ol className="list-decimal ml-5 text-sm space-y-1">
            {(w.app_review_checklist || []).map((it, i) => (
              <li key={i}>{it.replace(/^\d+\.\s*/, "")}</li>
            ))}
          </ol>
        </StepCard>

        {/* Step 7 — Set env vars */}
        <StepCard step={7} icon={KeyRound} title="Set backend env vars" testid="meta-wizard-step-7">
          <p className="text-sm text-muted-foreground mb-2">
            After the app is created, paste the App ID + App Secret from Meta into your backend <code>.env</code>:
          </p>
          <pre className="text-xs bg-neutral-900 text-neutral-100 p-3 overflow-auto">
{`META_APP_ID=<paste from Meta app basic settings>
META_APP_SECRET=<paste — keep it secret>
META_REDIRECT_URI=${w.redirect_uri}
META_GRAPH_VERSION=v25.0
META_SCOPES=ads_read,ads_management,business_management,pages_show_list,pages_read_engagement,pages_manage_metadata,leads_retrieval`}
          </pre>
          <p className="text-xs text-muted-foreground mt-2">
            Restart the backend (<code>sudo supervisorctl restart backend</code>) after setting these. This screen will refresh with a green banner when configured.
          </p>
        </StepCard>

        <div className="text-center pt-4">
          <Button variant="outline" className="rounded-none" onClick={load} data-testid="meta-wizard-refresh">
            Re-check configuration
          </Button>
        </div>
      </div>
    </div>
  );
}

function StepCard({ step, icon: Icon, title, children, testid }) {
  return (
    <div className="border" data-testid={testid}>
      <div className="border-b bg-neutral-50 px-3 py-2 flex items-center gap-2">
        <div className="bg-foreground text-background text-xs font-bold w-6 h-6 flex items-center justify-center">{step}</div>
        <Icon className="w-4 h-4" />
        <div className="font-semibold text-sm">{title}</div>
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}
